import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  Typography,
  Select,
  TextField,
  Button,
  Spinner,
  Alert,
  EmptyState,
} from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import {
  adminApiClient,
  type VendorApplicationItem,
  type VendorAppFilter,
} from '../lib/adminApiClient';
import { NEUTRAL_BTN } from '../lib/buttonStyles';
import { StoreChip, StageChip } from '../components/VendorChips';
import { PIPELINE_STAGES, TERMINAL_STAGES, storeTypeChip } from '../lib/vendorChips';
import { duplicateWarningLabel } from '../lib/vendorApplicationLifecycle';

/**
 * Vendor applications — list view (Figma 273:2). The onboarding queue: a row per
 * application with its store-type + pipeline-stage chips; "Review" opens the
 * detail/pipeline screen. A Board toggle switches to the kanban (300:2).
 */

const ALL_CATEGORIES = '__all__';
const GRID = '1.8fr 1.3fr 0.8fr 1fr 1fr 0.7fr';

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
function locationOf(app: VendorApplicationItem): string {
  const a = app.businessInfo.address;
  if (!a?.city && !a?.state) return '—';
  return [a?.city, a?.state].filter(Boolean).join(', ');
}
function nameOf(app: VendorApplicationItem): string {
  return app.businessInfo.businessName || 'Unnamed vendor';
}

function Centered({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>{children}</Box>;
}

const STAGE_OPTIONS = [
  { value: 'open', label: 'Open (in pipeline)' },
  { value: 'all', label: 'All stages' },
  ...PIPELINE_STAGES.map((s) => ({ value: s.status, label: s.label })),
  ...TERMINAL_STAGES.map((s) => ({ value: s.status, label: s.label })),
];

export default function VendorApplications() {
  const caps = useCapabilities();
  const canView = caps.has('vendor:read');
  const canReview = caps.has('vendor:review');
  const navigate = useNavigate();

  const [stage, setStage] = useState<VendorAppFilter>('open');
  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [sort, setSort] = useState<'newest' | 'oldest'>('newest');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<VendorApplicationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listVendorApplications({ status: stage });
      setItems(res.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load vendor applications');
    } finally {
      setLoading(false);
    }
  }, [stage]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  // Category options derived from the loaded page (distinct store types).
  const categoryOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const a of items) {
      const bt = (a.businessInfo.businessType ?? '').trim();
      if (bt) seen.set(bt.toLowerCase(), storeTypeChip(bt).label);
    }
    return [
      { value: ALL_CATEGORIES, label: 'All categories' },
      ...[...seen.entries()].map(([value, label]) => ({ value, label })),
    ];
  }, [items]);

  const visible = useMemo(() => {
    let list = items;
    if (category !== ALL_CATEGORIES) {
      list = list.filter((a) => (a.businessInfo.businessType ?? '').toLowerCase() === category);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((a) => nameOf(a).toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      const d = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      return sort === 'newest' ? d : -d;
    });
  }, [items, category, search, sort]);

  if (caps.loading)
    return (
      <Centered>
        <Spinner color="brand" />
      </Centered>
    );
  if (!canView)
    return <Alert severity="error">You don’t have permission to view vendor applications.</Alert>;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Vendor applications
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Vendor onboarding pipeline — review the applicant, build their price adapter, and approve
        them onto the marketplace.
      </Typography>

      {!canReview && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">View-only access — you can browse but can’t make changes.</Alert>
        </Box>
      )}

      {/* Search + Board toggle */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
        <TextField
          label="Search vendors by business name"
          ariaLabel="Search vendors"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 240 }}
        />
        <Button
          ariaLabel="Switch to board view"
          buttonText="Board view"
          variant="outlined"
          size="small"
          sx={NEUTRAL_BTN}
          onClick={() => navigate('/vendor-applications/board')}
        />
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Select
          ariaLabel="Sort"
          label="Sort"
          native
          size="small"
          sx={{ width: 180 }}
          value={sort}
          onChange={(_e, v) => setSort(v as 'newest' | 'oldest')}
          options={[
            { value: 'newest', label: 'Applied (newest)' },
            { value: 'oldest', label: 'Applied (oldest)' },
          ]}
        />
        <Select
          ariaLabel="Filter by category"
          label="Category"
          native
          size="small"
          sx={{ width: 180 }}
          value={category}
          onChange={(_e, v) => setCategory(v as string)}
          options={categoryOptions}
        />
        <Select
          ariaLabel="Filter by stage"
          label="Stage"
          native
          size="small"
          sx={{ width: 200 }}
          value={stage}
          onChange={(_e, v) => setStage(v as VendorAppFilter)}
          options={STAGE_OPTIONS}
        />
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Centered>
          <Spinner color="brand" />
        </Centered>
      ) : visible.length === 0 ? (
        <EmptyState
          icon="shop"
          title="No vendor applications"
          body="No applications match this filter."
        />
      ) : (
        <Box
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: '#fff',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: GRID,
              gap: 2,
              px: 2,
              py: 1.25,
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
            }}
          >
            {['Business', 'Location', 'Applied', 'Category', 'Stage', ''].map((h, i) => (
              <Typography
                key={i}
                variant="caption"
                sx={{ fontWeight: 700, color: '#64748b', letterSpacing: 0.4 }}
              >
                {h.toUpperCase()}
              </Typography>
            ))}
          </Box>
          {/* Rows */}
          {visible.map((app, idx) => (
            <Box
              key={app.applicationId}
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID,
                gap: 2,
                px: 2,
                py: 1.5,
                alignItems: 'center',
                borderBottom: idx < visible.length - 1 ? '1px solid #eef2f6' : 'none',
                '&:hover': { backgroundColor: '#fafbfc' },
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#1f2937' }}>{nameOf(app)}</Typography>
                {duplicateWarningLabel(app.duplicateBusinessWarning) && (
                  <Typography
                    variant="caption"
                    sx={{ color: '#9A6700', fontWeight: 700, display: 'block', mt: 0.25 }}
                  >
                    {duplicateWarningLabel(app.duplicateBusinessWarning)}
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {locationOf(app)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {fmtDate(app.createdAt)}
              </Typography>
              <Box>
                <StoreChip businessType={app.businessInfo.businessType} />
              </Box>
              <Box>
                <StageChip status={app.status} />
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Button
                  ariaLabel={`Review ${nameOf(app)}`}
                  buttonText="Review"
                  variant="outlined"
                  size="small"
                  sx={NEUTRAL_BTN}
                  onClick={() => navigate(`/vendor-applications/${app.applicationId}`)}
                />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
